
"use client";

import { signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { useAuth } from '@/components/providers/auth-provider';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LogIn, LogOut, User, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

export function AuthButton({ inSheet = false }: { inSheet?: boolean }) {
  const { user, userRoles, loading } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const router = useRouter();

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      // No need to push, onIdTokenChanged will handle it.
    } catch (error) {
      console.error("Error signing in with Google", error);
      toast({
        title: "Login Failed",
        description: "There was an error signing in. Please try again.",
        variant: "destructive",
      })
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      await fetch('/api/auth/session', { method: 'DELETE' });
      router.push('/logout');
    } catch (error) {
      console.error("Error signing out", error);
      toast({
        title: "Logout Failed",
        description: "There was an error signing out. Please try again.",
        variant: "destructive",
      })
    }
  };
  
  if (inSheet) {
    if (loading || !user) {
      return null;
    }

    return (
       <Button onClick={handleSignOut} variant="outline" className="w-full">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
      </Button>
    )
  }

  if (isMobile) {
    if (loading) {
      return <div className="h-5 w-5" aria-hidden="true" />;
    }

    // In mobile, the profile link is in MobileNav, so this can be a simple login button or null
    if (!user) {
       return (
        <Button variant="ghost" size="icon" onClick={handleSignIn} className="p-0 h-5 w-5">
          <LogIn className="h-5 w-5" />
        </Button>
      )
    }
    return null; // Or some other UI if needed when logged in
  }

  if (loading) {
    return <div className="h-9 w-[88px]" aria-hidden="true" />;
  }

  if (user) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                        <AvatarImage src={user.photoURL ?? ""} alt={user.displayName ?? "User"} />
                        <AvatarFallback>
                            <User className="h-4 w-4" />
                        </AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.displayName}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                        </p>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <Link href="/profile">
                  <DropdownMenuItem>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                  </DropdownMenuItem>
                </Link>
                {userRoles.includes('admin') && (
                  <Link href="/admin">
                    <DropdownMenuItem>
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Admin</span>
                    </DropdownMenuItem>
                  </Link>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
  }

  return (
    <Button onClick={handleSignIn}>
      <LogIn className="mr-2 h-4 w-4" />
      Login
    </Button>
  );
}
